import { useEffect, useRef, useState } from "react";

/**
 * Count up from 0 (or `from`) to `to` when the number enters the viewport.
 * Uses requestAnimationFrame + an ease-out curve. Respects reduced motion.
 */
export default function Counter({
  to,
  from = 0,
  duration = 1600,
  suffix = "",
  prefix = "",
  className = "",
  testId,
}) {
  const [value, setValue] = useState(from);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setValue(to);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min(1, (now - start) / duration);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(from + (to - from) * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
    // `IntersectionObserver`, `performance`, `requestAnimationFrame`, and the
    // `started` ref are stable; scanner over-reports non-reactive values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, from, duration]);

  return (
    <span ref={ref} data-testid={testId} className={className}>
      {prefix}
      {value.toLocaleString()}
      {suffix}
    </span>
  );
}
